/**
 * =============================================================================
 * ROASTBREW - API UTILITY
 * =============================================================================
 *
 * Centralized fetch wrapper with automatic token refresh on 401 responses.
 *
 * Three backends, one client:
 *   - User Server    (REACT_APP_API_URL)         → /auth, /accounts, /billing
 *   - Content API    (REACT_APP_CONTENT_API_URL) → /catalog, /video, /me, /progress
 *   - search-server  (REACT_APP_SEARCH_API_URL)  → /search
 * The path prefix picks the origin. If a backend's URL is unset, those paths fall
 * back to the User Server origin (i.e. the pre-split behavior).
 *
 * Credentials: User Server calls send `credentials: 'include'` because the refresh
 * token lives in an httpOnly cookie there. Content API and search-server calls
 * authenticate purely by Bearer token, so they send `credentials: 'omit'` — this is
 * what lets them keep a permissive `Access-Control-Allow-Origin` (browsers reject
 * `*` on a credentialed request, and search-server's allowlist likewise runs with
 * credentials disabled). The Authorization header is a manual header and is still
 * sent under 'omit'; only cookies are dropped.
 *
 * Content-Type: set to application/json EXCEPT when the body is a FormData. The
 * browser must set multipart/form-data itself, because only it knows the boundary
 * string — setting it by hand produces a body the server cannot parse. This is what
 * makes the /search/image upload work.
 *
 * USAGE:
 * import { createApiClient } from './utils/api';
 * const api = createApiClient({ getToken, setToken, onSessionExpired });
 * const data = await api.fetch('/accounts/me');
 *
 * =============================================================================
 */

const API_URL = process.env.REACT_APP_API_URL;
const CONTENT_API_URL = process.env.REACT_APP_CONTENT_API_URL || API_URL;
const SEARCH_API_URL = process.env.REACT_APP_SEARCH_API_URL || API_URL;

// Paths served by the Content API.
const CONTENT_PREFIXES = ['/catalog', '/video', '/me', '/progress'];

// Paths served by search-server.
const SEARCH_PREFIXES = ['/search'];

// Everything else goes to the User Server.
function matchesPrefix(path, prefixes) {
  return prefixes.some(
    (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)
  );
}

function isContentPath(path) {
  return matchesPrefix(path, CONTENT_PREFIXES);
}

function isSearchPath(path) {
  return matchesPrefix(path, SEARCH_PREFIXES);
}

// Bearer-only backends: no cookies cross the origin.
function usesBearerOnly(path) {
  return isContentPath(path) || isSearchPath(path);
}

function resolveBase(path) {
  if (isSearchPath(path)) return SEARCH_API_URL;
  if (isContentPath(path)) return CONTENT_API_URL;
  return API_URL;
}

export function createApiClient({ getToken, setToken, onSessionExpired }) {
  let isRefreshing = false;
  let refreshPromise = null;

  async function refreshToken() {
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    isRefreshing = true;

    refreshPromise = (async () => {
      try {
        // Refresh always targets the User Server — it issues the tokens and owns
        // the refresh-token cookie.
        const response = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          // Only a refusal (401/403: the refresh cookie is gone or rejected) ends the session. A 5xx or a
          // network error (fetch throws) keeps it: the token in hand is usually still valid (October 20, 2026).
          const err = new Error(response.status === 401 || response.status === 403 ? 'Refresh refused' : 'Refresh failed');
          err.refused = response.status === 401 || response.status === 403;
          throw err;
        }

        const data = await response.json();

        if (data.accessToken) {
          setToken(data.accessToken);
          return data.accessToken;
        }

        throw new Error('No access token in refresh response');
      } catch (err) {
        console.error('Token refresh failed:', err);
        if (err && err.refused) onSessionExpired();
        return null;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  async function apiFetch(path, options = {}) {
    const absolute = path.startsWith('http');
    const bearerOnly = !absolute && usesBearerOnly(path);
    const base = absolute ? '' : resolveBase(path);
    const url = absolute ? path : `${base}${path}`;

    // A FormData body must NOT carry a hand-set Content-Type: the browser adds
    // multipart/form-data plus the boundary it generated. Setting it manually
    // yields a body multer cannot parse.
    const isFormData =
      typeof FormData !== 'undefined' && options.body instanceof FormData;

    const makeRequest = (accessToken) => {
      const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      return fetch(url, {
        ...options,
        headers,
        // Content API and search-server are Bearer-only (no cookies) → omit.
        // User Server needs the refresh cookie → include.
        credentials: bearerOnly ? 'omit' : 'include',
      });
    };

    const currentToken = getToken();
    let response = await makeRequest(currentToken);

    if (response.status === 401 && currentToken) {
      const newToken = await refreshToken();

      if (newToken) {
        response = await makeRequest(newToken);
      }
    }

    return response;
  }

  return {
    fetch: apiFetch,
    refreshToken,
  };
}
